import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Activity } from "lucide-react";
import api from "../utils/api";
import { API_BASE } from "../config";

type User = {
  _id: string;
  name: string;
  email: string;
  number: string;
  refree: string;
  createdAt: string;
  active: boolean;
  investmentAllowed: boolean;
  selfIncomeActive: boolean;
  teamIncomeActive: boolean;
  role: string;
  kycStatus: 'pending' | 'verified' | 'rejected';
  referrerEmail?: string;
  lockUntil?: number;
};

type EditingUser = {
  name: string;
  number: string;
};

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<EditingUser>({
    name: "",
    number: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingKyc, setIsUpdatingKyc] = useState(false);
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  // Snackbar function
  const showSnackbar = useCallback((message: string, type: 'success' | 'error') => {
    setSnackbar({ show: true, message, type });
    setTimeout(() => {
      setSnackbar({ show: false, message: '', type: 'success' });
    }, 3000); // Hide after 3 seconds
  }, []);


  const fetchUsers = useCallback(async (page: number = 1) => {
    try {
      setIsLoading(true);
      const userResponse = await api.get(`/api/user/getUsers/all?page=${page}&limit=${pagination.limit}&filter=${filter}&search=${searchTerm}`);
      const userData = userResponse.data;

      if (userData.status === "success" && userData.user) {
        const usersArray: User[] = Object.values(userData.user);
        setUsers(usersArray);
        if (userData.pagination) {
          setPagination(userData.pagination);
        }
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.limit, filter, searchTerm]);

  useEffect(() => {
    fetchUsers(pagination.page);
  }, [pagination.page, fetchUsers]);

  // Handle filter change - reset to page 1
  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle search change - reset to page 1
  const handleSearchChange = (newSearch: string) => {
    setSearchTerm(newSearch);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const forceKycStatus = useCallback(async (email: string, status: 'verified' | 'rejected') => {
    setIsUpdatingKyc(true); // Start loader

    try {
      await api.patch(
        `/api/kyc/verify/${email}`,
        { status }
      );

      // Show success snackbar instead of updating UI state
      showSnackbar(`KYC ${status === 'verified' ? 'approved' : 'rejected'} successfully`, 'success');
      // Re-fetch users to update the KYC status in the UI
      fetchUsers();

    } catch (err) {
      showSnackbar('Failed to update KYC status', 'error');
      console.error(err);
    } finally {
      setIsUpdatingKyc(false); // Stop loader
    }
  }, [showSnackbar, fetchUsers]);


  const toggleActiveStatus = useCallback(async (email: string) => {
    try {
      const user = users.find((user) => user.email === email);
      if (!user) return;

      const isActive = !user.active;
      await api.put(`/api/user/toggleActiveStatusByEmail`, {
        email,
        active: isActive,
      });

      setUsers((prev) =>
        prev.map((user) => (user.email === email ? { ...user, active: isActive } : user))
      );
    } catch (error) {
      console.error("Failed to toggle active status:", error);
    }
  }, [users]);

  const toggleInvestmentStatus = useCallback(async (email: string) => {
    try {
      const user = users.find((user) => user.email === email);
      if (!user) return;

      const isAllowed = !user.investmentAllowed;
      await api.put(`/api/invest/toggle-investment-allowed/${email}`);

      setUsers((prev) =>
        prev.map((user) => (user.email === email ? { ...user, investmentAllowed: isAllowed } : user))
      );
    } catch (error) {
      console.error("Failed to toggle investment status:", error);
    }
  }, [users]);
  
  const toggleSelfIncomeStatus = useCallback(async (email: string) => {
    try {
      const user = users.find((user) => user.email === email);
      if (!user) return;

      const isAllowed = !user.selfIncomeActive;
      await api.put(`/api/user/toggle-self-income/${email}`);

      setUsers((prev) =>
        prev.map((user) => (user.email === email ? { ...user, selfIncomeActive: isAllowed } : user))
      );
    } catch (error) {
      console.error("Failed to toggle self income status:", error);
    }
  }, [users]);

  const toggleTeamIncomeStatus = useCallback(async (email: string) => {
    try {
      const user = users.find((user) => user.email === email);
      if (!user) return;

      const isAllowed = !user.teamIncomeActive;
      await api.put(`/api/user/toggle-team-income/${email}`);

      setUsers((prev) =>
        prev.map((user) => (user.email === email ? { ...user, teamIncomeActive: isAllowed } : user))
      );
    } catch (error) {
      console.error("Failed to toggle team income status:", error);
    }
  }, [users]);

  const startEditUser = useCallback((user: User) => {
    setEditingUser(user.email);
    setEditFormData({
      name: user.name,
      number: user.number,
    });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingUser(null);
    setEditFormData({
      name: "",
      number: "",
    });
  }, []);

  const saveUser = useCallback(async (email: string) => {
    if (!editFormData.name.trim() || !editFormData.number.trim()) {
      alert("Name and number are required fields");
      return;
    }

    try {
      setIsSaving(true);
      const response = await api.put(`/api/user/editUser/${email}`, {
        name: editFormData.name,
        number: editFormData.number,
      });
      if (response.data.status === "success") {
        // Update local state
        setUsers((prev) =>
          prev.map((user) =>
            user.email === email
              ? { ...user, name: editFormData.name, number: editFormData.number }
              : user
          )
        );

        setEditingUser(null);
        alert("User updated successfully!");
      }
    } catch (error: any) {
      console.error("Failed to update user:", error);
      alert(error.response?.data?.message || "Failed to update user");
    } finally {
      setIsSaving(false);
    }
  }, [editFormData.name, editFormData.number]);


  /**
   * Opens the user's account in a new tab.
   *
   * The tab is opened synchronously on the click; browsers block a window.open()
   * that runs after an await, because by then it no longer counts as a
   * user-initiated action — which made this button look like it did nothing.
   * The blank tab is pointed at the redirect once the server answers.
   */
  const impersonateUser = useCallback(async (email: string) => {
    const tab = window.open("", "_blank");
    try {
      const res = await api.post(`/api/adminlogin/impersonate`, { email });
      const url = res.data?.redirectUrl;
      if (!url) throw new Error("No redirect URL in response");

      if (tab) {
        tab.location.href = url;
      } else {
        // Popup blocked entirely — fall back to this tab.
        window.location.href = url;
      }
    } catch (error) {
      tab?.close();
      console.error("Failed to impersonate user:", error);
      alert("Admin session expired or unauthorized");
    }
  }, []);


  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 pb-20">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">User Management</h1>

      {/* Search & Filter Section */}
      <div className="mb-6 flex flex-col gap-4">
        <div className="relative group flex-grow">
          <input
            type="text"
            placeholder="Search email, name, or referrer..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full p-3 pl-4 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#523680]/20 border-gray-200 transition-all"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <select
            value={filter}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#523680]/20 border-gray-200 bg-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="active-invest">Invest Allowed</option>
            <option value="inactive-invest">Invest Stopped</option>
          </select>

          <select
            value={pagination.limit}
            onChange={(e) => {
              const newLimit = parseInt(e.target.value);
              setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
            }}
            className="w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#523680]/20 border-gray-200 bg-white"
          >
            <option value="10">10 / page</option>
            <option value="20">20 / page</option>
            <option value="50">50 / page</option>
            <option value="100">100 / page</option>
          </select>
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center p-12 text-gray-500 font-medium">No users match your criteria.</div>
        ) : (
          users.map((user) => (
            <div
              key={user._id}
              className="bg-white shadow-md rounded-lg p-5 border border-gray-200 hover:shadow-lg transition duration-300"
            >
              {editingUser === user.email ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={editFormData.name}
                        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                        className="w-full p-2 border rounded focus:outline-none focus:ring focus:ring-blue-300"
                        placeholder="Enter name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="text"
                        value={editFormData.number}
                        onChange={(e) => setEditFormData({ ...editFormData, number: e.target.value })}
                        className="w-full p-2 border rounded focus:outline-none focus:ring focus:ring-blue-300"
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>

                  <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                    <strong>Email:</strong> {user.email} (cannot be changed)
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => saveUser(user.email)}
                      disabled={isSaving}
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={isSaving}
                      className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex flex-col md:flex-row md:justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between md:justify-start gap-4">
                      <h2 className="text-lg font-bold text-[#523680] truncate">{user.name}</h2>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider ${user.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {user.active ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                      <p className="text-gray-600 flex items-center gap-2 overflow-hidden">
                        <span className="font-semibold shrink-0">Email:</span>
                        <span className="truncate" title={user.email}>{user.email}</span>
                      </p>
                      <p className="text-gray-600 flex items-center gap-2">
                        <span className="font-semibold shrink-0">Phone:</span> {user.number}
                      </p>
                      <p className="text-gray-600 flex items-center gap-2 overflow-hidden">
                        <span className="font-semibold shrink-0">Referrer:</span>
                        <span className="truncate">{user.referrerEmail || "—"}</span>
                      </p>
                      <p className="text-gray-600 flex items-center gap-2">
                        <span className="font-semibold shrink-0">Joined:</span>
                        {new Date(user.createdAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                    </div>

                    {user.lockUntil && user.lockUntil > Date.now() && (
                      <div className="mt-2 inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-red-50 text-red-700 border border-red-100">
                        <span className="w-1.5 h-1.5 mr-2 bg-red-500 rounded-full animate-pulse"></span>
                        Locked until {new Date(user.lockUntil).toLocaleTimeString()}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 shrink-0 md:min-w-[160px]">
                    <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
                      <button
                        onClick={() => startEditUser(user)}
                        className="px-3 py-2 text-xs font-medium bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 border border-blue-100 transition-colors"
                      >
                        Edit User
                      </button>

                      <button
                        onClick={() => impersonateUser(user.email)}
                        className="px-3 py-2 text-xs font-medium bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 border border-purple-100 transition-colors"
                      >
                        Login as User
                      </button>

                      <button
                        onClick={() => navigate(`/user-logs/${user.email}`)}
                        className="px-3 py-2 text-xs font-bold bg-[#523680] text-white rounded-lg hover:bg-[#432c69] transition-all shadow-sm flex items-center justify-center gap-1.5"
                      >
                        <Activity className="w-3.5 h-3.5" />
                        User Activity
                      </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
                      <button
                        onClick={() => toggleActiveStatus(user.email)}
                        className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors border ${user.active
                          ? "bg-green-50 text-green-600 border-green-100 hover:bg-green-100"
                          : "bg-red-50 text-red-600 border-red-100 hover:bg-red-100"
                          }`}
                      >
                        {user.active ? "Enabled" : "Disabled"}
                      </button>

                      <button
                        onClick={() => toggleInvestmentStatus(user.email)}
                        className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors border ${user.investmentAllowed
                          ? "bg-green-50 text-green-600 border-green-100 hover:bg-green-100"
                          : "bg-red-50 text-red-600 border-red-100 hover:bg-red-100"
                          }`}
                      >
                        Invest {user.investmentAllowed ? "ON" : "OFF"}
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => toggleSelfIncomeStatus(user.email)}
                        className={`px-3 py-2 text-[11px] font-medium rounded-lg transition-colors border ${user.selfIncomeActive !== false
                          ? "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100"
                          : "bg-orange-50 text-orange-600 border-orange-100 hover:bg-orange-100"
                          }`}
                      >
                        Self Inc {user.selfIncomeActive !== false ? "ON" : "OFF"}
                      </button>

                      <button
                        onClick={() => toggleTeamIncomeStatus(user.email)}
                        className={`px-3 py-2 text-[11px] font-medium rounded-lg transition-colors border ${user.teamIncomeActive !== false
                          ? "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100"
                          : "bg-orange-50 text-orange-600 border-orange-100 hover:bg-orange-100"
                          }`}
                      >
                        Team Inc {user.teamIncomeActive !== false ? "ON" : "OFF"}
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => forceKycStatus(user.email, 'verified')}
                        disabled={isUpdatingKyc}
                        className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-tight bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                      >
                        Approve KYC
                      </button>

                      <button
                        onClick={() => forceKycStatus(user.email, 'rejected')}
                        disabled={isUpdatingKyc}
                        className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-tight bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                      >
                        Reject KYC
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {!isLoading && users.length > 0 && (
        <div className="flex flex-col md:flex-row justify-center items-center gap-4 mt-8 mb-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors font-medium text-gray-700"
            >
              Previous
            </button>

            <div className="flex items-center space-x-1 shrink-0 overflow-x-auto max-w-[150px] sm:max-w-none no-scrollbar">
              {(() => {
                const pages = [];
                const maxVisible = window.innerWidth < 640 ? 3 : 5;
                let start = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
                let end = Math.min(pagination.totalPages, start + maxVisible - 1);

                if (end - start + 1 < maxVisible) {
                  start = Math.max(1, end - maxVisible + 1);
                }

                if (start > 1) {
                  pages.push(
                    <button
                      key={1}
                      onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                      className="w-10 h-10 rounded-lg border hover:bg-gray-50 transition-colors text-gray-600"
                    >
                      1
                    </button>
                  );
                  if (start > 2) pages.push(<span key="dots-start" className="px-1 text-gray-400">...</span>);
                }

                for (let i = start; i <= end; i++) {
                  if (i <= 0) continue;
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setPagination(prev => ({ ...prev, page: i }))}
                      className={`w-10 h-10 rounded-lg border transition-all ${pagination.page === i
                        ? "bg-blue-600 text-white border-blue-600 shadow-md transform scale-105"
                        : "hover:bg-gray-50 text-gray-700"
                        }`}
                    >
                      {i}
                    </button>
                  );
                }

                if (end < pagination.totalPages) {
                  if (end < pagination.totalPages - 1) pages.push(<span key="dots-end" className="px-1 text-gray-400">...</span>);
                  pages.push(
                    <button
                      key={pagination.totalPages}
                      onClick={() => setPagination(prev => ({ ...prev, page: pagination.totalPages }))}
                      className="w-10 h-10 rounded-lg border hover:bg-gray-50 transition-colors text-gray-600"
                    >
                      {pagination.totalPages}
                    </button>
                  );
                }

                return pages;
              })()}
            </div>

            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
              disabled={pagination.page === pagination.totalPages}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors font-medium text-gray-700"
            >
              Next
            </button>
          </div>

          <div className="flex items-center space-x-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg border shadow-sm">
            <span>Go to:</span>
            <input
              type="number"
              min="1"
              max={pagination.totalPages}
              className="w-16 p-1 border rounded text-center focus:ring focus:ring-blue-100 outline-none"
              placeholder="#"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const p = parseInt((e.target as HTMLInputElement).value);
                  if (!isNaN(p) && p >= 1 && p <= pagination.totalPages) {
                    setPagination(prev => ({ ...prev, page: p }));
                  }
                }
              }}
            />
            <span className="whitespace-nowrap">of {pagination.totalPages} ({pagination.total} total)</span>
          </div>
        </div>
      )}
      {snackbar.show && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${snackbar.type === 'success'
          ? 'bg-green-600 text-white'
          : 'bg-red-600 text-white'
          }`}>
          <div className="flex items-center space-x-2">
            {snackbar.type === 'success' ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
            <span>{snackbar.message}</span>
          </div>
        </div>
      )}
      {isUpdatingKyc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-700">Updating KYC status...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;